import * as React from "react";
import { IconButton, Tooltip } from "@mui/material";

import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "@/components/icons";
import { brand } from "@/theme";
import { repoTokens } from "@/theme/tokens";

const SIZE = repoTokens.shell.collapseButton;

/** A side column's collapse / expand control: a round 28px button half
 *  outside the column's inner edge, vertically centred, the same place in
 *  both states. ``side`` is the column it belongs to: the left column's
 *  sits on its right edge, the right column's on its left edge, with the
 *  arrows mirrored. Its parent must be positioned and must not clip. */
export function ShellCollapseButton({
  side,
  collapsed,
  onToggle,
  collapseLabel,
  expandLabel,
  controls,
}: {
  side: "left" | "right";
  collapsed: boolean;
  onToggle: () => void;
  collapseLabel: string;
  expandLabel: string;
  /** The id of the column it collapses (``aria-controls``). */
  controls: string;
}): React.JSX.Element {
  const label = collapsed ? expandLabel : collapseLabel;
  // The arrow points the way the column moves.
  const pointsLeft = side === "left" ? !collapsed : collapsed;
  return (
    <Tooltip title={label} placement={side === "left" ? "right" : "left"}>
      <IconButton
        onClick={onToggle}
        aria-label={label}
        aria-expanded={!collapsed}
        aria-controls={controls}
        sx={{
          position: "absolute",
          top: "50%",
          ...(side === "left" ? { right: -SIZE / 2 } : { left: -SIZE / 2 }),
          transform: "translateY(-50%)",
          zIndex: 3,
          width: SIZE,
          height: SIZE,
          p: 0,
          borderRadius: "50%",
          bgcolor: brand.elevated,
          border: `1px solid ${brand.line}`,
          color: repoTokens.colors.icon,
          "&:hover": { bgcolor: repoTokens.colors.elevatedHover },
        }}
      >
        {pointsLeft ? (
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
